import { useMemo } from "react"

import { format } from "date-fns"

import CostBreakdownCard, { type CostLine } from "@/components/shared/CostBreakdownCard"
import { findRouteInstanceOn } from "@/lib/plannedRouteMapping"
import {
  applyPlannedMileageMultiplier,
  calculateDistanceFromPlannedStops,
  calculateDurationMinutes,
  formatChargingMilesBreakdown,
  priceRoute,
} from "@/lib/routeCostCalculation"
import type {
  CollectionsConfig,
  PlannedRoute,
  RouteInstance,
  VehicleType,
} from "@/types/collections"

import type { RouteFormData } from "./types"

/** Priced from the day's generated instance where there is one, otherwise from the planned stops and form times. */
export function RouteCostPreview({
  route,
  isCreatingNewRoute,
  form,
  selectedDate,
  vehicleTypes,
  config,
  routeInstances,
}: {
  route: PlannedRoute | null
  isCreatingNewRoute: boolean
  form: RouteFormData
  selectedDate: Date
  vehicleTypes: VehicleType[] | undefined
  config: CollectionsConfig | undefined
  routeInstances: RouteInstance[] | undefined
}) {
  const priced = useMemo(() => {
    if (!vehicleTypes || !config) return null

    let distanceMiles: number | null = null
    let durationMinutes: number | null = null

    if (route && !isCreatingNewRoute && routeInstances) {
      const routeInstance = findRouteInstanceOn(routeInstances, route.id, selectedDate)

      if (routeInstance) {
        distanceMiles = routeInstance.distance_miles ?? null
        durationMinutes = routeInstance.duration_minutes ?? null
      }
    }

    if (
      distanceMiles == null &&
      route?.stops &&
      route.stops.length >= 2
    ) {
      const raw = calculateDistanceFromPlannedStops(route.stops)
      distanceMiles = applyPlannedMileageMultiplier(raw, config) ?? raw
    }

    if (durationMinutes == null && form.startTime && form.endTime) {
      const dateStr = format(selectedDate, "yyyy-MM-dd")
      const startDateTime = new Date(`${dateStr}T${form.startTime}`)
      const endDateTime = new Date(`${dateStr}T${form.endTime}`)
      durationMinutes = calculateDurationMinutes(startDateTime, endDateTime)
    } else if (
      durationMinutes == null &&
      route?.plannedStartTime &&
      route?.plannedEndTime
    ) {
      durationMinutes = calculateDurationMinutes(
        route.plannedStartTime,
        route.plannedEndTime,
      )
    }

    const cost = priceRoute({
      vehicleTypes,
      vehicleTypeRef: form.vehicleType,
      config,
      distanceMiles,
      durationMinutes,
      plan: {
        stops: route?.stops,
        // The form, not the saved route, so the figure moves before saving.
        crewSize: form.crewSize,
        routeKind: form.routeKind,
      },
    })

    if (!cost) return null
    return { cost, distanceMiles, durationMinutes }
  }, [route, isCreatingNewRoute, form, selectedDate, vehicleTypes, config, routeInstances])

  if (!priced) return null
  const { cost, distanceMiles, durationMinutes } = priced

  // Direct-injection routes are priced, not costed: one agreed figure, reported as the wage line.
  const lines: CostLine[] = cost.is_direct_injection_priced
    ? [
        {
          kind: "wage",
          label: "Driver Wage",
          detail: "Agreed direct injection price",
          value: cost.driver_wage_cost,
        },
      ]
    : [
        { kind: "lease", label: "Vehicle Lease", value: cost.vehicle_lease_cost },
        {
          kind: "charging",
          label: "Charging Cost",
          detail:
            distanceMiles != null && distanceMiles > 0
              ? (formatChargingMilesBreakdown(cost) ?? `${distanceMiles.toFixed(1)} miles`)
              : route?.stops && route.stops.length >= 2
                ? "Est. from stops"
                : "Requires 2+ stops",
          value: cost.charging_cost > 0 ? cost.charging_cost : null,
        },
        {
          kind: "wage",
          label: "Driver Wage",
          detail:
            durationMinutes != null && durationMinutes > 0
              ? `${Math.round((durationMinutes / 60) * 10) / 10}h`
              : (form.startTime && form.endTime) ||
                  (route?.plannedStartTime && route?.plannedEndTime)
                ? "Est. from times"
                : "Requires planned times",
          value: cost.driver_wage_cost > 0 ? cost.driver_wage_cost : null,
        },
      ]

  return (
    <div className="mt-4 pt-4 border-t">
      <CostBreakdownCard
        density="full"
        title="Estimated Route Cost"
        subtitle="Based on current configuration"
        lines={lines}
        total={cost.total_cost}
        revenue={cost.total_haulage_delivery_revenue}
        perParcel={{ cost: cost.cost_per_parcel, parcels: cost.total_forecast_volume }}
      />
    </div>
  )
}
